import { Link, useNavigate } from "@tanstack/react-router";
import { useReadOnly } from "../api/users.js";
import { ArrowLeft } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCreateFirewall } from "../api/firewalls.js";
import { useServers } from "../api/servers.js";
import { ErrorMessage, fieldError } from "../ui/feedback.js";
import { PageHeader } from "../ui/layout.js";
import { Alert, Button, Card, Spinner, TextArea, TextField } from "../ui/primitives.js";
import { useToast } from "../ui/toast.js";
import {
  defaultInbound,
  hasErrors,
  RuleEditor,
  rulesOf,
  type RuleDraft,
} from "./firewall-rules.js";
import { parseLabels } from "./labels.js";

/** Label selectors typed one per line. */
export const selectorsOf = (text: string): string[] =>
  text
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

function Step({
  n,
  title,
  lead,
  children,
}: {
  n: number;
  title: string;
  lead?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <section className="flex flex-col gap-3" aria-labelledby={`step-${n}`}>
        <div className="flex items-start gap-3">
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-accent-strong">
            {n}
          </span>
          <div className="flex flex-col gap-0.5">
            <h2 id={`step-${n}`} className="text-base font-semibold">
              {title}
            </h2>
            {lead && <p className="text-sm text-muted">{lead}</p>}
          </div>
        </div>
        {children}
      </section>
    </Card>
  );
}

/** Servers to pick with checkboxes (create page and the apply dialog). */
export function ServerChecklist({
  servers,
  chosen,
  onChange,
}: {
  servers: { id: string; name: string; zone?: string; labels?: Record<string, string> }[];
  chosen: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
}) {
  return (
    <ul className="flex flex-col divide-y divide-line rounded-lg border border-line">
      {servers.map((s) => (
        <li key={s.id}>
          <label className="flex min-h-11 cursor-pointer items-center gap-3 px-3 py-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-accent"
              checked={chosen.has(s.id)}
              onChange={() => {
                const next = new Set(chosen);
                if (next.has(s.id)) next.delete(s.id);
                else next.add(s.id);
                onChange(next);
              }}
            />
            <span className="min-w-0 flex-1 truncate font-medium" title={s.name}>
              {s.name}
            </span>
            {s.labels && Object.keys(s.labels).length > 0 && (
              <span className="hidden truncate font-mono text-xs text-muted sm:inline">
                {Object.entries(s.labels)
                  .map(([k, v]) => `${k}=${v}`)
                  .join(", ")}
              </span>
            )}
          </label>
        </li>
      ))}
    </ul>
  );
}

export function FirewallCreatePage({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const servers = useServers(projectId);
  const create = useCreateFirewall(projectId);
  const [name, setName] = useState("");
  const [drafts, setDrafts] = useState<RuleDraft[]>(defaultInbound);
  const [serverIds, setServerIds] = useState<Set<string>>(new Set());
  const [selectors, setSelectors] = useState("");
  const [labelsText, setLabelsText] = useState("");
  const [tried, setTried] = useState(false);
  const { readOnly, reason: readOnlyReason, token: tokenReadOnly } = useReadOnly(projectId);

  const labels = parseLabels(labelsText);
  const inbound = drafts.filter((d) => d.direction === "in").length;
  const outbound = drafts.length - inbound;
  const selectorList = selectorsOf(selectors);
  const invalid = hasErrors(drafts) || labels.labels === null || !name.trim();

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTried(true);
    if (invalid || !labels.labels) return;
    create.mutate(
      {
        name: name.trim(),
        rules: rulesOf(drafts),
        serverIds: [...serverIds],
        labelSelectors: selectorList,
        labels: labels.labels,
      },
      {
        onSuccess: (firewall) => {
          toast({ tone: "ok", title: t("firewalls.created", { name: firewall.name }) });
          void navigate({
            to: "/projects/$projectId/firewalls/$firewallId",
            params: { projectId, firewallId: firewall.id },
          });
        },
      },
    );
  };

  return (
    <>
      <Link
        to="/projects/$projectId/firewalls"
        params={{ projectId }}
        className="inline-flex min-h-6 items-center gap-1 text-sm text-muted hover:text-text"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {t("firewalls.back")}
      </Link>
      <PageHeader
        title={t("firewalls.newTitle")}
        intro={t("firewalls.newIntro")}
        help="firewalls"
      />
      {tokenReadOnly && <Alert tone="warn">{readOnlyReason}</Alert>}
      <form
        onSubmit={submit}
        noValidate
        className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start"
      >
        <div className="flex min-w-0 flex-col gap-4">
          <Step n={1} title={t("firewalls.stepName")}>
            <TextField
              label={t("firewalls.name")}
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={128}
              hint={t("firewalls.nameHint")}
              error={
                tried && !name.trim()
                  ? t("errors.fieldInvalid")
                  : fieldError(create.error, "name", t)
              }
            />
          </Step>
          <Step n={2} title={t("firewalls.stepInbound")} lead={t("firewalls.stepInboundLead")}>
            <RuleEditor direction="in" drafts={drafts} onChange={setDrafts} showErrors={tried} />
          </Step>
          <Step n={3} title={t("firewalls.stepOutbound")} lead={t("firewalls.stepOutboundLead")}>
            <RuleEditor direction="out" drafts={drafts} onChange={setDrafts} showErrors={tried} />
          </Step>
          <Step n={4} title={t("firewalls.stepApply")} lead={t("firewalls.stepApplyLead")}>
            <h3 className="text-sm font-medium">{t("firewalls.servers")}</h3>
            {servers.isPending ? (
              <Spinner label={t("common.loading")} />
            ) : servers.data?.length ? (
              <ServerChecklist servers={servers.data} chosen={serverIds} onChange={setServerIds} />
            ) : (
              <p className="text-sm text-muted">{t("firewalls.noServersInProject")}</p>
            )}
            <TextArea
              label={t("firewalls.labelSelectors")}
              name="labelSelectors"
              value={selectors}
              onChange={(e) => setSelectors(e.target.value)}
              rows={2}
              spellCheck={false}
              placeholder="role=web"
              hint={t("firewalls.labelSelectorsHint")}
              error={fieldError(create.error, "labelSelectors", t)}
            />
          </Step>
          <Step n={5} title={t("firewalls.stepLabels")}>
            <TextArea
              label={t("networks.labels")}
              name="labels"
              value={labelsText}
              onChange={(e) => setLabelsText(e.target.value)}
              rows={2}
              spellCheck={false}
              placeholder="env=production"
              hint={t("networks.labelsHint")}
              error={
                labels.badLine !== null
                  ? t("networks.labelsInvalid", { line: labels.badLine })
                  : undefined
              }
            />
          </Step>
        </div>
        <aside className="lg:sticky lg:top-20">
          <Card>
            <div className="flex flex-col gap-3 text-sm">
              <h2 className="text-base font-semibold">{t("firewalls.summary")}</h2>
              <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1">
                <dt className="text-muted">{t("firewalls.summaryInbound")}</dt>
                <dd>{inbound}</dd>
                <dt className="text-muted">{t("firewalls.summaryOutbound")}</dt>
                <dd>{outbound || t("firewalls.summaryOutboundAll")}</dd>
                <dt className="text-muted">{t("firewalls.summaryServers")}</dt>
                <dd>{serverIds.size}</dd>
                <dt className="text-muted">{t("firewalls.summarySelectors")}</dt>
                <dd>{selectorList.length}</dd>
              </dl>
              {serverIds.size + selectorList.length === 0 && (
                <p className="text-xs text-muted">{t("firewalls.summaryNotApplied")}</p>
              )}
              {tried && invalid && <Alert tone="danger">{t("firewalls.fixErrors")}</Alert>}
              <ErrorMessage error={create.error} />
              <Button type="submit" busy={create.isPending} disabled={readOnly}>
                {t("firewalls.submit")}
              </Button>
            </div>
          </Card>
        </aside>
      </form>
    </>
  );
}
